<template>
  <div class="highlights-wrapper">
    <!-- Header with Filters -->
    <div class="highlights-header">
      <h2 class="highlights-title">{{ $t('globals.highlights') || 'Highlights' }}</h2>
      <div class="header-controls">
        <!-- Period Filter Buttons -->
        <div class="period-filters">
          <button
            v-for="p in periods"
            :key="p.value"
            :class="['period-btn', { active: selectedPeriod === p.value }]"
            @click="onPeriodChange(p.value)"
          >
            {{ p.label }}
          </button>
        </div>

        <!-- Date Range Picker -->
        <div class="date-range-picker">
          <input
            type="date"
            v-model="dateFrom"
            @change="onDateChange"
            class="date-input"
          />
          <span class="date-separator">-</span>
          <input
            type="date"
            v-model="dateTo"
            @change="onDateChange"
            class="date-input"
          />
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div v-if="!isLoading && metricsData.length > 0" class="summary-stats-card">
      <div class="stats-header">
        <span class="stats-label">{{ $t('globals.data') }}:</span>
        <span class="stats-period-label current">{{ $t('globals.current') }}:</span>
        <span class="stats-period-label past">{{ $t('globals.past') }}:</span>
      </div>
      <div class="stats-row">
        <span class="stats-label">{{ $t('globals.new_inquiries') }}:</span>
        <span class="stats-value current">{{ currentPeriodStats.new_inquiries }}</span>
        <span class="stats-value past">{{ previousPeriodStats.new_inquiries }}</span>
      </div>
      <div class="stats-row">
        <span class="stats-label">{{ $t('globals.new_orders') }}:</span>
        <span class="stats-value current">{{ currentPeriodStats.new_orders }}</span>
        <span class="stats-value past">{{ previousPeriodStats.new_orders }}</span>
      </div>
      <div class="stats-row">
        <span class="stats-label">{{ $t('globals.new_customers') }}:</span>
        <span class="stats-value current">{{ currentPeriodStats.new_customers }}</span>
        <span class="stats-value past">{{ previousPeriodStats.new_customers }}</span>
      </div>
      <div class="stats-row">
        <span class="stats-label">{{ $t('globals.sold_devices') }}:</span>
        <span class="stats-value current">{{ currentPeriodStats.sold_devices }}</span>
        <span class="stats-value past">{{ previousPeriodStats.sold_devices }}</span>
      </div>
      <div class="stats-row">
        <span class="stats-label">{{ $t('globals.invoices_sent') }}:</span>
        <span class="stats-value current">{{ currentPeriodStats.invoices_sent }}</span>
        <span class="stats-value past">{{ previousPeriodStats.invoices_sent }}</span>
      </div>
    </div>

    <!-- Chart Card -->
    <div class="chart-card">
      <div class="chart-header">
        <h3 class="chart-title">Metrics Overview</h3>
        <div class="refresh-button" @click="fetchData">
          <icon name="sync-alt" :spin="isLoading"></icon>
        </div>
      </div>

      <div class="chart-container">
        <div v-if="isLoading" class="loading-state">
          <icon name="spinner" spin class="spinner"></icon>
          <p>Loading metrics data...</p>
        </div>
        <ulu-line-chart
          v-else
          :chart-data="chartData"
          :options="chartOptions"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { format, subDays, subWeeks, subMonths, subQuarters, subYears, addDays } from "date-fns";

import "vue-awesome/icons/spinner";
import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
    "ulu-line-chart": () => import("@/components/common/charts/LineChart")
  },
  data() {
    return {
      isLoading: false,
      selectedPeriod: "daily",
      dateFrom: format(subDays(new Date(), 7), "yyyy-MM-dd"),
      dateTo: format(new Date(), "yyyy-MM-dd"),
      metricsData: [],
      periods: [
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "quarterly", label: "Quarterly" },
        { value: "yearly", label: "Yearly" }
      ]
    };
  },
  computed: {
    ...mapGetters(["getUserSystem"]),
    currentPeriodStats() {
      if (!this.metricsData || this.metricsData.length === 0) {
        return {
          new_inquiries: 0,
          new_orders: 0,
          new_customers: 0,
          sold_devices: 0,
          invoices_sent: 0
        };
      }

      // Get the last (most recent) data point
      const lastDataPoint = this.metricsData[this.metricsData.length - 1];
      return {
        new_inquiries: lastDataPoint.new_inquiries,
        new_orders: lastDataPoint.new_orders,
        new_customers: lastDataPoint.new_customers,
        sold_devices: lastDataPoint.sold_devices,
        invoices_sent: lastDataPoint.invoices_sent
      };
    },
    previousPeriodStats() {
      if (!this.metricsData || this.metricsData.length < 2) {
        return {
          new_inquiries: 0,
          new_orders: 0,
          new_customers: 0,
          sold_devices: 0,
          invoices_sent: 0
        };
      }

      // Get the second-to-last (previous) data point
      const previousDataPoint = this.metricsData[this.metricsData.length - 2];
      return {
        new_inquiries: previousDataPoint.new_inquiries,
        new_orders: previousDataPoint.new_orders,
        new_customers: previousDataPoint.new_customers,
        sold_devices: previousDataPoint.sold_devices,
        invoices_sent: previousDataPoint.invoices_sent
      };
    },
    chartData() {
      if (!this.metricsData || this.metricsData.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      const labels = this.metricsData.map(item => {
        const date = new Date(item.date);
        if (this.selectedPeriod === "daily") {
          return format(date, "MMM dd");
        } else if (this.selectedPeriod === "weekly") {
          return format(date, "MMM dd");
        } else if (this.selectedPeriod === "monthly") {
          return format(date, "MMM yyyy");
        } else if (this.selectedPeriod === "quarterly") {
          return format(date, "QQQ yyyy");
        } else {
          return format(date, "yyyy");
        }
      });

      return {
        labels,
        datasets: [
          {
            label: "New Inquiries",
            data: this.metricsData.map(item => item.new_inquiries),
            borderColor: "#5469d4",
            backgroundColor: "rgba(84, 105, 212, 0.1)",
            fill: false,
            tension: 0.4
          },
          {
            label: "New Orders",
            data: this.metricsData.map(item => item.new_orders),
            borderColor: "#48bb78",
            backgroundColor: "rgba(72, 187, 120, 0.1)",
            fill: false,
            tension: 0.4
          },
          {
            label: "New Customers",
            data: this.metricsData.map(item => item.new_customers),
            borderColor: "#ed8936",
            backgroundColor: "rgba(237, 137, 54, 0.1)",
            fill: false,
            tension: 0.4
          },
          {
            label: "Sold Devices",
            data: this.metricsData.map(item => item.sold_devices),
            borderColor: "#9f7aea",
            backgroundColor: "rgba(159, 122, 234, 0.1)",
            fill: false,
            tension: 0.4
          },
          {
            label: "Invoices Sent",
            data: this.metricsData.map(item => item.invoices_sent),
            borderColor: "#f56565",
            backgroundColor: "rgba(245, 101, 101, 0.1)",
            fill: false,
            tension: 0.4
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        plugins: {
          legend: {
            display: true,
            position: "top"
          },
          tooltip: {
            mode: "index",
            intersect: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0
            }
          }
        }
      };
    }
  },
  methods: {
    ...mapActions(["fetchMetricsTimeSeries"]),
    async fetchData() {
      this.isLoading = true;

      try {
        // Add 1 day to dateTo to include the full day in the query
        const dateToInclusive = format(addDays(new Date(this.dateTo), 1), "yyyy-MM-dd");

        const response = await this.fetchMetricsTimeSeries({
          date_from: this.dateFrom,
          date_to: dateToInclusive,
          period: this.selectedPeriod
        });

        if (response.data && response.data.data && response.data.data.metricsTimeSeries) {
          this.metricsData = JSON.parse(response.data.data.metricsTimeSeries);
        } else {
          this.metricsData = [];
        }
      } catch (error) {
        console.error("Error fetching metrics time series:", error);
        this.metricsData = [];
      } finally {
        this.isLoading = false;
      }
    },
    onPeriodChange(period) {
      this.selectedPeriod = period;
      this.updateDateRange();
      this.fetchData();
    },
    onDateChange() {
      this.fetchData();
    },
    updateDateRange() {
      const today = new Date();
      let fromDate = today;

      switch (this.selectedPeriod) {
        case "daily":
          fromDate = subDays(today, 7);
          break;
        case "weekly":
          fromDate = subWeeks(today, 12);
          break;
        case "monthly":
          fromDate = subMonths(today, 12);
          break;
        case "quarterly":
          fromDate = subQuarters(today, 8);
          break;
        case "yearly":
          fromDate = subYears(today, 5);
          break;
      }

      this.dateFrom = format(fromDate, "yyyy-MM-dd");
      this.dateTo = format(today, "yyyy-MM-dd");
    }
  },
  watch: {
    getUserSystem() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.highlights-wrapper {
  background-color: #f7f7f7;
  min-height: calc(100vh - 200px);
  padding: 24px;
}

.highlights-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.highlights-title {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.period-filters {
  display: flex;
  gap: 8px;
  background: white;
  padding: 4px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.period-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.period-btn:hover {
  background-color: #edf2f7;
}

.period-btn.active {
  background-color: #5469d4;
  color: white;
}

.date-range-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.date-input {
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  outline: none;
}

.date-input:focus {
  border-color: #5469d4;
  box-shadow: 0 0 0 3px rgba(84, 105, 212, 0.1);
}

.date-separator {
  color: #a0aec0;
  font-weight: 500;
}

.summary-stats-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  width: 50%;
  max-width: 600px;
}

.stats-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
  font-weight: 600;
  font-size: 0.875rem;
  color: #4a5568;
}

.stats-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f7fafc;
}

.stats-row:last-child {
  border-bottom: none;
}

.stats-label {
  color: #2d3748;
  font-size: 0.9375rem;
}

.stats-period-label {
  min-width: 80px;
  text-align: right;
}

.stats-period-label.current {
  color: #5469d4;
}

.stats-period-label.past {
  color: #718096;
}

.stats-value {
  min-width: 80px;
  text-align: right;
  font-weight: 600;
  font-size: 1rem;
}

.stats-value.current {
  color: #5469d4;
}

.stats-value.past {
  color: #718096;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.refresh-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.2s;
}

.refresh-button:hover {
  background-color: #edf2f7;
  border-color: #5469d4;
  color: #5469d4;
}

.chart-container {
  position: relative;
  width: 100%;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #718096;
}

.loading-state .spinner {
  font-size: 32px;
  margin-bottom: 12px;
  color: #5469d4;
}

.loading-state p {
  margin: 0;
  font-size: 14px;
}

@media (max-width: 768px) {
  .highlights-wrapper {
    padding: 16px;
  }

  .highlights-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-controls {
    width: 100%;
  }

  .period-filters {
    width: 100%;
    overflow-x: auto;
  }

  .date-range-picker {
    width: 100%;
  }

  .stats-header,
  .stats-row {
    grid-template-columns: 1fr 60px 60px;
    gap: 8px;
    font-size: 0.8125rem;
  }

  .stats-period-label,
  .stats-value {
    min-width: 60px;
  }

  .summary-stats-card {
    width: 100%;
    max-width: 100%;
  }

}
</style>
